import React, { useState, useMemo, useEffect, useRef } from "react";
import { message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  updateCreationPages,
  removeCreationPage,
} from "../../../store/slices/pageSlice";
import {
  Plus,
  X,
  ChevronDownIcon,
  ChevronUpIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { deleteS3Object } from "../../../services/backend/s3Api";
import { renderGrid, getByPath } from "../../common/FormRenderer";
import { validateProjectPageEditForm } from "../../../config/projectPageEdit.validation";
import {
  usePicklistMap,
  resolvePicklistFields,
} from "../../../hooks/usePicklist";
import { PP_PICKLIST_LABELS } from "../../project-page/projectPageColumnsConfig";
import { SOBJECT_PROJECT_PAGE } from "../../../config/sobjectNames";

const ProjectPages = () => {
  const dispatch = useDispatch();
  const pages = useSelector((state) => state.page.creation.pages);
  const objectives = useSelector(
    (state) => state.projectObjective.creation.objectives,
  );

  const { picklistMap: pagePicklistMap } = usePicklistMap(
    Object.values(PP_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE,
  );

  const objectiveOptions = useMemo(() => {
    const opts = [];
    if (objectives?.objectiveName) {
      opts.push({
        label: objectives.objectiveName,
        value: objectives.objectiveId,
      });
    }
    if (Array.isArray(objectives)) {
      objectives.forEach((obj) => {
        if (obj.objectiveName)
          opts.push({
            label: obj.objectiveName,
            value: obj.objectiveId,
          });
      });
    }
    return opts;
  }, [objectives]);

  const metadata = useSelector((state) => state.project.creation.metadata);
  const isCreating = useSelector((state) => state.navigation.isCreating);
  const [showModal, setShowModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    type: true,
    projectSpecific: true,
    system: false,
  });

  const getProjectDisplayName = () => {
    return (
      (metadata?.name && String(metadata.name).trim()) ||
      (metadata?.shortName && String(metadata.shortName).trim()) ||
      (metadata?.contributorProjectName &&
        String(metadata.contributorProjectName).trim()) ||
      ""
    );
  };

  const [form, setForm] = useState({
    projectPageName: "",
    projectPageType: "",
    duplicateCheck: false,
    active: true,
    qualificationStep: "",
    projectPageDescription: "",
    project: getProjectDisplayName(),
    projectObjective: "",
    createdBy: "",
    lastModifiedBy: "",
  });

  const [isQualifyingPage, setIsQualifyingPage] = useState(
    form.projectPageType === "Project Qualifying Page",
  );

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      project: getProjectDisplayName(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata?.name, metadata?.shortName, metadata?.contributorProjectName]);

  useEffect(() => {
    if (!isCreating) return;
    const display = getProjectDisplayName();
    if (!display) return;
    if (
      Array.isArray(pages) &&
      pages.some((p) => (p?.project || "") !== display)
    ) {
      const updated = pages.map((p) => ({ ...p, project: display }));
      dispatch(updateCreationPages(updated));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metadata?.name,
    metadata?.shortName,
    metadata?.contributorProjectName,
    isCreating,
    pages?.length,
  ]);

  useEffect(() => {
    if (form.projectPageType === "Project Qualifying Page")
      setIsQualifyingPage(true);
    else setIsQualifyingPage(false);
  }, [form.projectPageType]);

  const sections = {
    type: {
      title: "Type",
      key: "type",
      fields: [
        {
          label: "Project Page Type",
          path: "projectPageType",
          type: "select",
          required: true,
          picklistLabel: PP_PICKLIST_LABELS.TYPE,
          info: "Select the type of project page",
        },
        {
          label: "Active",
          path: "active",
          type: "checkbox",
        },
        {
          label: "Project Page Description",
          path: "projectPageDescription",
          type: "textarea",
          info: "Optional description of this page",
        },
        ...(isQualifyingPage
          ? [
              {
                label: "Qualification Step",
                path: "qualificationStep",
                type: "searchable",
                searchType: "qualificationStep",
                required: true,
                info: "Search and Select Qualification Step",
              },
            ]
          : []),
      ],
    },
    projectSpecific: {
      title: "Project Specific",
      key: "projectSpecific",
      fields: [
        isCreating
          ? {
              label: "Project",
              path: "project",
              type: "text",
              readOnly: true,
              required: true,
              info: "Auto-filled from Step 1. Edit Project Metadata to change.",
            }
          : {
              label: "Project",
              path: "project",
              type: "searchable",
              searchType: "project",
              required: true,
              info: "Search and select project",
            },
        {
          label: "Project Objective",
          path: "projectObjective",
          type: "select",
          options: objectiveOptions,
          info: "Select project objective",
        },
      ],
    },
    system: {
      title: "System Information",
      key: "system",
      fields: [
        {
          label: "Created By",
          path: "createdBy",
          type: "text",
          readOnly: true,
          info: "System field - filled on save",
        },
        {
          label: "Last Modified By",
          path: "lastModifiedBy",
          type: "text",
          readOnly: true,
          info: "System field - filled on save",
        },
      ],
    },
  };

  const validateForm = () => {
    const validationPayload = {
      info: {
        type: form.projectPageType,
        projectId: metadata?.id || "TEMP",
      },
    };

    const validationErrors = validateProjectPageEditForm(validationPayload);

    const errors = [];
    Object.entries(validationErrors).forEach(([path, msg]) => {
      const fieldLabelMap = {
        "info.type": "Project Page Type",
      };
      const fieldMap = {
        "info.type": "projectPageType",
      };
      const field = fieldMap[path] || path;
      const fieldLabel = fieldLabelMap[path] || path;

      const needsFieldLabel = [
        "This field is required",
        "Must be an integer",
        "Must be a number",
        "Invalid format",
      ].includes(msg);

      const finalMessage = needsFieldLabel ? `${fieldLabel}: ${msg}` : msg;

      errors.push({ field, message: finalMessage });
    });

    if (isCreating) {
      const projectDisplay = getProjectDisplayName();
      if (!projectDisplay || String(projectDisplay).trim() === "") {
        errors.push({
          field: "project",
          message: "Project: This field is required",
        });
      }
    }

    return errors;
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const savePage = (shouldKeepOpen = false) => {
    const errors = validateForm();

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    if (form?.id) {
      const updatedPages = pages.map((p) =>
        p.id === form.id ? { ...form } : p,
      );
      dispatch(updateCreationPages(updatedPages));
      if (!shouldKeepOpen) {
        setShowModal(false);
        resetForm();
      } else {
        resetForm();
      }
      return;
    }

    const newPageId = Date.now();
    const newPage = {
      id: newPageId,
      ...form,
    };

    const updatedPages = Array.isArray(pages) ? [...pages, newPage] : [newPage];

    dispatch(updateCreationPages(updatedPages));

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setForm({
      projectPageName: "",
      projectPageType: "",
      duplicateCheck: false,
      active: true,
      projectPageDescription: "",
      project: getProjectDisplayName(),
      projectObjective: "",
      createdBy: "",
      lastModifiedBy: "",
    });
    setValidationErrors([]);
  };

  const openModal = () => {
    resetForm();
    const projectDisplay = getProjectDisplayName();
    if (!projectDisplay || String(projectDisplay).trim() === "") {
      message.warning(
        "Project is empty. Please fill Project Name on Step 1; this field auto-fills from there.",
      );
    }
    setShowModal(true);
  };

  const handleEdit = (page) => {
    setForm({ ...page });
    setShowModal(true);
  };

  const handleRemove = (id) => {
    if (window.confirm("Are you sure you want to remove this project page?")) {
      dispatch(removeCreationPage(id));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (path, value) => {
    setForm((prev) => ({ ...prev, [path]: value }));
  };

  const SectionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      {expandedSections[section] ? (
        <ChevronUpIcon className="w-4 h-4 text-gray-500" />
      ) : (
        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
      )}
    </button>
  );

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Pages</h2>
        <p className="text-gray-600">Define pages for your project workflow</p>
      </div>

      {pages.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Current Project Pages ({pages.length})
          </h3>
          <div className="space-y-3">
            {pages.map((page) => (
              <div
                key={page.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between group"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {page.projectPageType}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Active: {page.active ? "Yes" : "No"}
                    {page.project && ` | Project: ${page.project}`}
                    <br />
                    {page.name}
                    {page.id}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(page)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit project page"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(page.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove project page"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={openModal}
        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
        style={{ backgroundColor: "#08979c" }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#0ab0b7")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#08979c")}
      >
        <Plus className="w-5 h-5" />
        New Project Page
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                New Project Page
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {validationErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-red-800 font-semibold mb-2">
                    Please fix the following errors:
                  </h4>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-4">
                {Object.values(sections).map((section) => (
                  <div key={section.key}>
                    <SectionHeader
                      title={section.title}
                      section={section.key}
                    />
                    {expandedSections[section.key] && (
                      <div className="mt-3">
                        {renderGrid(
                          resolvePicklistFields(
                            section.fields,
                            pagePicklistMap,
                          ),
                          {
                            formData: form,
                            onChange: handleChange,
                            showValidation: validationErrors.length > 0,
                          },
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => savePage(true)}
                className="px-4 py-2"
                style={{
                  backgroundColor: "#08979c",
                  color: "white",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#0ab0b7")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#08979c")
                }
              >
                Save & New
              </button>
              <button
                type="button"
                onClick={() => savePage(false)}
                className="px-4 py-2"
                style={{
                  backgroundColor: "#08979c",
                  color: "white",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#0ab0b7")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#08979c")
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPages;
